const DIVISION_FIELDS_FRAGMENT = `
fragment divisionFields on Division {
  id
  name
}
`

const PRODUCT_TAG_FIELDS_FRAGMENT = `
fragment productTagFields on ProductTag {
  id
  name
  color
  productsCount
}
`

const INVENTORY_EVENTS_FIELDS_FRAGMENT = `
fragment inventoryEvents on InventoryEvent {
  id
  eventType
  amount
  stockValue
  branchStockValue
  eventCreatedAt
  unit
  locationId
  createdByUser {
    id
    name
  }
  additional
}
`

const INVENTORY_FIELDS_FRAGMENT = `
id
inventory
lockToken
notifyThreshold
price
priceVisible
quantity
threshold
unit
truckLocationThreshold
binLocationThreshold
technicianThreshold
product {
  id
  name
  distributor
  photo
}
`

export const PRODUCTS_QUERY = `
${DIVISION_FIELDS_FRAGMENT}
${PRODUCT_TAG_FIELDS_FRAGMENT}

query FindProducts(
  $branchId: Int!, $includeLocationItems: Boolean = false, $productTagId: Int, $untaggedOnly: Boolean
) {
  manager {
    account {
      id
      truckInventoryFeature
      hasInvoices
      divisions {
        ...divisionFields
      }
      distributorsList
      erpCatalogs
    }

    currentBranch: branches(id: $branchId) {
      truckInventoryFeature
      productTags {
        ...productTagFields
      }
      untaggedProductsCount
      inventoryProducts(productTagId: $productTagId, untaggedOnly: $untaggedOnly) {
        createdAt
        updatedAt
        divisions {
          ...divisionFields
        }
        id
        price
        priceVisible
        quantity
        inventory
        lockToken
        threshold
        notifyThreshold
        unit
        updatedAt
        product {
          id
          code
          name
          description
          photo
          custom
          distributor
          erpCatalogName
        }
        originalProductData {
          unit
          price
        }
        truckLocationThreshold
        binLocationThreshold
        technicianThreshold
        productTags {
          ...productTagFields
        }
        locationItems @include(if: $includeLocationItems) {
          id
          locationId
          inventory
          threshold
          location {
            id
            code
            description
            locationType
            isDefault
            user {
              id
              name
            }
          }
        }
      }
    }
  }
}
`

export const INTERNAL_PRODUCTS_QUERY = `
${DIVISION_FIELDS_FRAGMENT}
${PRODUCT_TAG_FIELDS_FRAGMENT}

query FindProducts(
  $branchId: Int!, $accountId: Int!, $includeLocationItems: Boolean = false, $productTagId: Int, $untaggedOnly: Boolean
) {
  internal {
    accounts(id: $accountId) {
      nodes {
        id
        truckInventoryFeature
        hasInvoices
        divisions {
          ...divisionFields
        }
        distributorsList

        currentBranch: branches(id: $branchId) {
          truckInventoryFeature
          productTags {
            ...productTagFields
          }
          untaggedProductsCount
          inventoryProducts(productTagId: $productTagId, untaggedOnly: $untaggedOnly) {
            createdAt
            updatedAt
            divisions {
              ...divisionFields
            }
            id
            price
            priceVisible
            quantity
            inventory
            lockToken
            threshold
            notifyThreshold
            unit
            updatedAt
            product {
              id
              code
              name
              description
              photo
              custom
              distributor
              erpCatalogName
            }
            originalProductData {
              unit
              price
            }
            truckLocationThreshold
            binLocationThreshold
            technicianThreshold
            productTags {
              ...productTagFields
            }
            locationItems @include(if: $includeLocationItems) {
              id
              locationId
              inventory
              threshold
              location {
                id
                code
                description
                locationType
                isDefault
                user {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
}
`

export const SALABLE_PRODUCTS_LIST_QUERY = `
  query FindProducts($branchId: Int!, $textCode: String, $textName: String) {
    manager {
      account {
        salableCatalog(branchId: $branchId, textCode: $textCode, textName: $textName) {
          id
          code
          description
          distributor
          name
          photo
          unit
          price
        }
      }
    }
  }
`

export const INTERNAL_SALABLE_PRODUCTS_LIST_QUERY = `
  query FindInternalProducts($accountId: Int!, $branchId: Int!, $textCode: String, $textName: String) {
    internal {
      accounts(id: $accountId) {
        nodes {
          salableCatalog(branchId: $branchId, textCode: $textCode, textName: $textName) {
            id
            code
            description
            distributor
            name
            photo
            unit
            price
          }
        }
      }
    }
  }
`

export const BRANCHES_PRODUCTS_LIST_QUERY = `
  query FindProducts($branchId: Int!, $textCode: String, $textName: String) {
    manager {
      account {
        branchesCatalog(branchId: $branchId, textCode: $textCode, textName: $textName) {
          id
          code
          description
          distributor
          name
          photo
          unit
          price
        }
      }
    }
  }
`

export const INTERNAL_BRANCHES_PRODUCTS_LIST_QUERY = `
  query FindInternalProducts($accountId: Int!, $branchId: Int!, $textCode: String, $textName: String) {
    internal {
      accounts(id: $accountId) {
        nodes {
          branchesCatalog(branchId: $branchId, textCode: $textCode, textName: $textName) {
            id
            code
            description
            distributor
            name
            photo
            unit
            price
          }
        }
      }
    }
  }
`

export const INVENTORY_EVENTS_INTERNAL_QUERY = `
  ${INVENTORY_EVENTS_FIELDS_FRAGMENT}

  query FindInternalProduct(
    $accountId: Int
    $branchId: Int!
    $productId: Int!
    $first: Int
    $after: String
    $eventTypes: [InventoryEventTypesEnum!]
    $locationId: Int
    $throughEventId: Int
  ) {
    internal {
      accounts(id: $accountId) {
        nodes {
          currentBranch: branches(id: $branchId) {
            inventoryProducts(productId: $productId) {
              id
              product {
                code
              }
              inventory
              updatedAt
              inventoryEvents(
                first: $first
                after: $after
                eventTypes: $eventTypes
                locationId: $locationId
                throughEventId: $throughEventId
              ) {
                pageInfo {
                  endCursor
                  startCursor
                  hasNextPage
                  hasPreviousPage
                }
                nodes {
                  ...inventoryEvents
                }
              }
            }
          }
        }
      }
    }
  }
`

export const INVENTORY_EVENTS_MANAGER_QUERY = `
  ${INVENTORY_EVENTS_FIELDS_FRAGMENT}

  query FindProducts(
    $branchId: Int!
    $productId: Int!
    $first: Int
    $after: String
    $eventTypes: [InventoryEventTypesEnum!]
    $locationId: Int
    $throughEventId: Int
  ) {
    manager {
      currentBranch: branches(id: $branchId) {
        inventoryProducts(productId: $productId) {
          id
          product {
            code
          }
          inventory
          updatedAt
          inventoryEvents(
            first: $first
            after: $after
            eventTypes: $eventTypes
            locationId: $locationId
            throughEventId: $throughEventId
          ) {
            pageInfo {
              endCursor
              startCursor
              hasNextPage
              hasPreviousPage
            }
            nodes {
              ...inventoryEvents
            }
          }
        }
      }
    }
  }
`

export const HISTORY_LOCATIONS_INTERNAL_QUERY = `
  query FindInternalHistoryLocations(
    $accountId: Int
    $branchId: Int!
    $productId: Int!
  ) {
    internal {
      accounts(id: $accountId) {
        nodes {
          currentBranch: branches(id: $branchId) {
            inventoryProducts(productId: $productId) {
              historyLocations {
                id
                code
              }
            }
          }
        }
      }
    }
  }
`

export const HISTORY_LOCATIONS_MANAGER_QUERY = `
  query FindHistoryLocations($branchId: Int!, $productId: Int!) {
    manager {
      currentBranch: branches(id: $branchId) {
        inventoryProducts(productId: $productId) {
          historyLocations {
            id
            code
          }
        }
      }
    }
  }
`

export const PRODUCT_UPDATE_QUERY = `
mutation InventoryProductUpdate(
  $branchId: Int!,
  $attributes: InventoryProductUpdateAttributes!,
  $productId: Int!,
  $lockToken: String
) {
  inventoryProductUpdate(
    branchId: $branchId,
    attributes: $attributes,
    productId: $productId,
    lockToken: $lockToken
  ) {
    errors
    result {
      ${INVENTORY_FIELDS_FRAGMENT}
      productTags { id name color }
    }
  }
}
`

export const BRANCH_BIN_LOCATION_ITEMS_QUERY = `
  query BranchBinLocationItems($branchId: Int!, $productId: Int!) {
    branchBinLocationItems(branchId: $branchId, productId: $productId) {
      locationItemId
      locationCode
      inventory
      userName
    }
  }
`

export const PRODUCT_TRANSFER_QUERY = `
mutation InventoryProductTransfer(
  $sourceBranchId: Int!,
  $destinationBranchId: Int!,
  $attributes: InventoryProductTransferAttributes!,
  $productCode: String!
) {
  inventoryProductTransfer(
    sourceBranchId: $sourceBranchId,
    destinationBranchId: $destinationBranchId,
    attributes: $attributes,
    productCode: $productCode
  ) {
    errors
    result {
      ${INVENTORY_FIELDS_FRAGMENT}
    }
  }
}
`

export const PRODUCT_CREATE_QUERY = `
mutation InventoryProductCreate($attributes: InventoryProductCreateAttributes!) {
  inventoryProductCreate(attributes: $attributes) {
    errors
    result {
      ${INVENTORY_FIELDS_FRAGMENT}
      productTags { id name color }
    }
  }
}
`

export const PRODUCT_REMOVE_QUERY = `
mutation InventoryProductRemove($branchId: Int!, $productId: Int!) {
  inventoryProductRemove(branchId: $branchId, productId: $productId) {
    errors
  }
}
`

export const PRODUCT_INVENTORY_QUERY = `
  query ProductInventory($branchId: Int!, $productId: Int!) {
    manager {
      currentBranch: branches(id: $branchId) {
        inventoryProducts(productId: $productId) {
          id
          inventory
          lockToken
          updatedAt
        }
      }
    }
  }
`

export const PRODUCT_INVENTORY_INTERNAL_QUERY = `
  query ProductInventory($branchId: Int!, $productId: Int!, $accountId: Int!) {
    internal {
      accounts(id: $accountId) {
        nodes {
          currentBranch: branches(id: $branchId) {
            inventoryProducts(productId: $productId) {
              id
              inventory
              lockToken
              updatedAt
            }
          }
        }
      }
    }
  }
`

export const PRODUCT_TAG_CREATE_QUERY = `
mutation ProductTagCreate($branchId: Int!, $name: String!, $color: String!) {
  productTagCreate(branchId: $branchId, name: $name, color: $color) {
    errors
    result {
      id
      name
      color
    }
  }
}
`

export const PRODUCT_TAG_UPDATE_QUERY = `
mutation ProductTagUpdate($id: Int!, $name: String, $color: String) {
  productTagUpdate(id: $id, name: $name, color: $color) {
    errors
    result {
      id
      name
      color
    }
  }
}
`

export const PRODUCT_TAG_DELETE_QUERY = `
mutation ProductTagDelete($id: Int!) {
  productTagDelete(id: $id) {
    errors
    result {
      id
    }
  }
}
`
